import { useEffect, useRef, useState } from "react";
import "./ContactModal.css";

const INITIAL_FORM = { name: "", phone: "", city: "" };
// const API_URL =
//   process.env.REACT_APP_API_URL ||
//   process.env.REACT_APP_PRODUCTION_API_URL ||
//   "https://api.dwelloradesigns.com";

// const API_URL = "http://localhost:3001/api/designer-contact";
const API_URL = "https://api.dwelloradesigns.com";

function getSubmissionId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function ContactModal({ isOpen, closeModal }) {
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [isModalScrolling, setIsModalScrolling] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("contact-modal-open");
    dialogRef.current?.querySelector("input")?.focus();

    const dialog = dialogRef.current;
    let scrollTimeout;
    const handleScroll = () => {
      setIsModalScrolling(true);
      window.clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => setIsModalScrolling(false), 700);
    };

    dialog?.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      dialog?.removeEventListener("scroll", handleScroll);
      window.clearTimeout(scrollTimeout);
      document.body.classList.remove("contact-modal-open");
      setIsModalScrolling(false);
    };
  }, [isOpen, closeModal]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^[A-Za-z ]{2,}$/.test(formData.name.trim())) {
      nextErrors.name = "Enter a valid name using letters only.";
    }
    if (!/^[6-9][0-9]{9}$/.test(formData.phone.trim())) {
      nextErrors.phone = "Enter a valid 10-digit Indian mobile number.";
    }
    if (!formData.city) nextErrors.city = "Please select your city.";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === "submitting" || !validate()) return;
    setStatus("submitting");
    try {
      const response = await fetch(`${API_URL}/api/designer-contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Submission-Id": getSubmissionId(),
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          city: formData.city,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        const validationErrors = result.errors ? Object.values(result.errors).join(" ") : "";
        throw new Error(validationErrors || result.message || "Unable to submit enquiry.");
      }
      setStatus("success");
      setFormData(INITIAL_FORM);
      setErrors({});
    } catch (error) {
      setErrors({
        form: error.message || "Unable to submit enquiry. Please try again.",
      });
      setStatus("error");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="contact-modal-overlay"
      onMouseDown={(event) =>
        event.target === event.currentTarget && closeModal()
      }
    >
      <div
        className={`contact-modal-box ${isModalScrolling ? "is-scrolling" : ""}`}
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-modal-title"
      >
        <div className="contact-modal-benefits">
          <p className="contact-modal-kicker">Dwellora designs</p>
          <h2>Speak with Architects &amp; Designers </h2>
          <div className="contact-modal-points">
            <div className="contact-modal-point">
              <span aria-hidden="true">&#10003;</span>
              <div>
                <h3 style={{ marginBottom: '20px' }}>One Point of Contact</h3>
                {/* <p>
                 Design, specialists, site work and coordination — everything managed by Dwellora, so you don't have to chase multiple vendors.
                </p> */}
              </div>
            </div>
            <div className="contact-modal-point">
              <span aria-hidden="true">&#10003;</span>
              <div>
                <h3>Design + Execution, Seamlessly Connected</h3>
                {/* <p>Our design team coordinates with specialist partners and on-site crews to keep the design, quality and execution aligned.</p> */}
              </div>
            </div>
            <div className="contact-modal-point">
              <span aria-hidden="true">&#10003;</span>
              <div>
                <h3>Transparent, Package-Wise Pricing</h3>
                {/* <p>
                 Clear scope and package-wise costing, finalized with you before work begins — so there are no surprises along the way.
                </p> */}
              </div>
            </div>
          </div>
        </div>

        <div className="contact-modal-form-panel">
          <button
            type="button"
            className="contact-modal-close"
            onClick={closeModal}
            aria-label="Close contact form"
          >
            &#10005;
          </button>
          {status === "success" ? (
            <div className="contact-modal-success" role="status">
              <span className="contact-modal-success-icon">&#10003;</span>
              <h2>Thank you for reaching out.</h2>
              <p>Our design team will contact you shortly.</p>
              <button
                type="button"
                className="contact-modal-submit"
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <h2 id="contact-modal-title">Contact Information</h2>
              <p className="contact-modal-subtitle">
                One step closer to your perfect home
              </p>
              <form onSubmit={handleSubmit} noValidate>
                <label htmlFor="contact-name">Full Name *</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(event) =>
                    /^[A-Za-z ]*$/.test(event.target.value) &&
                    handleChange(event)
                  }
                  className={errors.name ? "has-error" : ""}
                  autoComplete="name"
                />
                {errors.name && (
                  <p className="contact-form-error">{errors.name}</p>
                )}
                <label htmlFor="contact-phone">Phone Number *</label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  inputMode="numeric"
                  placeholder="Enter your phone number"
                  value={formData.phone}
                  onChange={(event) =>
                    /^[0-9]{0,10}$/.test(event.target.value) &&
                    handleChange(event)
                  }
                  className={errors.phone ? "has-error" : ""}
                  autoComplete="tel"
                />
                {errors.phone && (
                  <p className="contact-form-error">{errors.phone}</p>
                )}
                <label htmlFor="contact-city">Select City *</label>
                <select
                  id="contact-city"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className={errors.city ? "has-error" : ""}
                >
                  <option value="" disabled>
                    Select your city
                  </option>
                  <option value="Pune">Pune</option>
                  <option value="Nashik">Nashik</option>
                  <option value="Latur">Latur</option>
                  <option value="Sambhajinagar">Sambhajinagar</option>
                </select>
                {errors.city && (
                  <p className="contact-form-error">{errors.city}</p>
                )}
                {errors.form && (
                  <p className="contact-form-error contact-form-error--form">
                    {errors.form}
                  </p>
                )}
                <button
                  type="submit"
                  className="contact-modal-submit"
                  disabled={status === "submitting"}
                >
                  {status === "submitting" ? "Submitting..." : "Submit"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
